"use client";

import { useEffect } from "react";

import { initUserSession, trackEvent } from "@/utils/analytics";

// ----------------------------------------------------------------------------
// Site-wide analytics auto-tracker. Mounts once in the root layout and uses
// a single delegated click listener on the body to instrument every
// tel:, mailto:, and resource-download click on the site — including links
// inside dynamically-rendered Strapi content where we don't control the JSX.
//
// Why a delegated listener instead of inline onClick per link?
//   - Strapi rich-text bodies render <a> tags from prose-markdown — we can't
//     attach handlers to those at the React layer.
//   - Adding tracking to every <Link> / <a> at the call site would mean
//     touching 9+ files for tel/mailto and unknown N for resource downloads.
//   - One mount = full coverage, including future links nobody remembers
//     to instrument.
//
// Consent is enforced inside trackEvent (drops the push if the analytics
// cookie isn't accepted).
// ----------------------------------------------------------------------------

const RESOURCE_PATH_PATTERNS = [
  /^\/resources\/sample-report/,
  /^\/resources\/case-study/,
  /^\/resources\/e-book/,
  /^\/resources\/datasheet/,
  /^\/resources\/data-sheet/,
  /^\/resources\/use-case/,
  /\.pdf(\?|$|#)/i,
];

const isResourceLink = (href: string): boolean => {
  // Strip origin so relative + absolute URLs hit the same checks.
  let path = href;
  try {
    const url = new URL(href, window.location.origin);
    if (url.host !== window.location.host && !/\.pdf(\?|$|#)/i.test(href)) {
      // External non-PDF: not a Certbar resource download.
      return false;
    }
    path = url.pathname + url.search + url.hash;
  } catch {
    // Relative href that won't parse — fall through with raw value.
  }
  return RESOURCE_PATH_PATTERNS.some((re) => re.test(path));
};

const inferResourceCategory = (href: string): string => {
  if (/sample-report/.test(href)) return "sample_report";
  if (/case-study/.test(href)) return "case_study";
  if (/e-book/.test(href)) return "e_book";
  if (/data-?sheet/.test(href)) return "data_sheet";
  if (/use-case/.test(href)) return "use_case";
  if (/\.pdf/i.test(href)) return "pdf";
  return "other";
};

// FAQ accordion items are rendered with `id="faq-<slug>"` (see
// FaqSection.tsx) — that gives us a clean selector to detect opens
// without per-component instrumentation. Mantine's accordion toggles
// `data-active="true"` on the open item; we capture clicks on the
// inner button and resolve the new state on the next frame.
const findFaqId = (el: HTMLElement | null): string | undefined => {
  const item = el?.closest?.('[id^="faq-"]');
  return item?.id?.replace(/^faq-/, "");
};

// Mantine Tabs.Tab renders as a button with role="tab" inside a [role="tablist"].
// Capturing here means every tab-style section on the site (industries,
// services tabs, etc.) reports tab_change without code changes.
const findTabInfo = (
  el: HTMLElement | null,
): { value: string; list_id?: string } | undefined => {
  const tab = el?.closest?.('[role="tab"]') as HTMLElement | null;
  if (!tab) return;
  const value =
    tab.getAttribute("data-value") ||
    tab.textContent?.trim().slice(0, 60) ||
    "unknown";
  const list = tab.closest('[role="tablist"]') as HTMLElement | null;
  return { value, list_id: list?.id || undefined };
};

const AnalyticsAutoTrack = () => {
  useEffect(() => {
    // Bootstrap user session (first-touch traffic origin + new/returning).
    // Safe to call on every mount; internally no-ops after first run.
    initUserSession();

    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // 1. tel: / mailto: / resource downloads — anchor-based events.
      const anchor = target.closest?.("a") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href") || "";
        if (href) {
          if (href.startsWith("tel:")) {
            trackEvent("phone_click", {
              number: href.replace(/^tel:/, "").trim(),
              link_text: anchor.textContent?.trim().slice(0, 60) || "",
            });
            return;
          }
          if (href.startsWith("mailto:")) {
            const email = href.replace(/^mailto:/, "").split("?")[0].trim();
            trackEvent("email_click", {
              // domain is segmentable; full address is PII so we don't send it
              email_domain: email.split("@")[1] || "",
              link_text: anchor.textContent?.trim().slice(0, 60) || "",
            });
            return;
          }
          if (isResourceLink(href)) {
            trackEvent("download_resource", {
              resource_url: href.slice(0, 200),
              resource_category: inferResourceCategory(href),
              link_text: anchor.textContent?.trim().slice(0, 60) || "",
            });
            return;
          }
        }
      }

      // 2. FAQ accordion open — fires on every click into a faq-* item.
      // Dedupes naturally because we only fire when the item is becoming
      // active (Mantine sets data-state after the click in a microtask).
      const faqSlug = findFaqId(target);
      if (faqSlug) {
        // Use requestAnimationFrame so Mantine's data-state is updated.
        requestAnimationFrame(() => {
          const item = document.getElementById(`faq-${faqSlug}`);
          // Only count opens, not collapses
          if (item?.querySelector('[data-state="open"]')) {
            trackEvent("faq_open", { faq_slug: faqSlug });
          }
        });
        return;
      }

      // 3. Tab change — captures any [role="tab"] across the site.
      const tabInfo = findTabInfo(target);
      if (tabInfo) {
        trackEvent("tab_change", {
          tab_value: tabInfo.value,
          tab_list_id: tabInfo.list_id,
        });
        return;
      }
    };

    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return null;
};

export default AnalyticsAutoTrack;
